import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  BRAND_ACTIVATION_REFRESH_EVENT,
  isBrandChecklistComplete,
} from '../../utils/brand/brandActivationGate';
import {
  getInfluencerChecklistProgress,
  getInfluencerChecklistVisibleTasks,
  INFLUENCER_ACTIVATION_REFRESH_EVENT,
  isInfluencerChecklistComplete,
} from '../../utils/influencer/influencerActivationGate';
import { navigateToOnboardingTask } from '../../utils/shared/onboardingTaskNavigation';

const activationCache = {
  influencer: null,
  brand: null,
};

const lastSidebarTasks = {
  influencer: [],
  brand: [],
};

export function resetActivationSidebarCache() {
  activationCache.influencer = null;
  activationCache.brand = null;
  lastSidebarTasks.influencer = [];
  lastSidebarTasks.brand = [];
}

export function getCachedActivation(userType) {
  return activationCache[userType] || null;
}

export function writeCachedActivation(userType, data) {
  activationCache[userType] = data;
}

const getActivationConfig = (userType) => {
  if (userType === 'influencer') {
    return {
      apiPath: '/api/influencer/activation',
      refreshEvent: INFLUENCER_ACTIVATION_REFRESH_EVENT,
    };
  }
  return {
    apiPath: '/api/brand/activation',
    refreshEvent: BRAND_ACTIVATION_REFRESH_EVENT,
  };
};

const getProgress = (userType, activation) => {
  if (userType === 'influencer') {
    return getInfluencerChecklistProgress(activation);
  }
  return activation?.progress ?? 0;
};

const isComplete = (userType, activation) => {
  if (userType === 'influencer') {
    return isInfluencerChecklistComplete(activation);
  }
  return isBrandChecklistComplete(activation);
};

const getVisibleTasks = (userType, activation) => {
  if (userType === 'influencer') {
    return getInfluencerChecklistVisibleTasks(
      activation,
      activation?.tasks?.length || 0,
    );
  }
  return (activation?.tasks || []).filter((task) => !task.done);
};

export function countIncompleteActivationTasks(userType, activation) {
  if (!userType || !activation || activation.dismissed || isComplete(userType, activation)) {
    return 0;
  }
  return getVisibleTasks(userType, activation).filter((task) => !task.locked).length;
}

async function fetchActivationPayload(userType) {
  const token = localStorage.getItem('token');
  if (!token) {
    writeCachedActivation(userType, null);
    lastSidebarTasks[userType] = [];
    return null;
  }

  const { apiPath } = getActivationConfig(userType);
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${apiPath}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) return undefined;
    const data = await response.json();
    writeCachedActivation(userType, data);
    return data;
  } catch {
    return undefined;
  }
}

export function useIncompleteActivationCount(userType) {
  const [count, setCount] = useState(() => (
    countIncompleteActivationTasks(userType, getCachedActivation(userType))
  ));

  useEffect(() => {
    if (!userType) {
      setCount(0);
      return undefined;
    }

    const applyCount = (activation) => {
      setCount(countIncompleteActivationTasks(userType, activation));
    };

    applyCount(getCachedActivation(userType));

    const loadActivation = async () => {
      const data = await fetchActivationPayload(userType);
      if (data === undefined) {
        applyCount(getCachedActivation(userType));
        return;
      }
      applyCount(data);
    };

    loadActivation();
    const { refreshEvent } = getActivationConfig(userType);
    window.addEventListener(refreshEvent, loadActivation);
    return () => window.removeEventListener(refreshEvent, loadActivation);
  }, [userType]);

  return count;
}

function useActivationSidebarTasks({
  userType,
  navigate,
  onNavigate,
}) {
  const location = useLocation();
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  const onNavigateRef = useRef(onNavigate);
  locationRef.current = location;
  navigateRef.current = navigate;
  onNavigateRef.current = onNavigate;

  const [activation, setActivation] = useState(() => activationCache[userType] || null);
  const { refreshEvent } = getActivationConfig(userType);

  const fetchActivation = useCallback(async () => {
    const data = await fetchActivationPayload(userType);
    if (data === undefined) return;
    setActivation(data);
  }, [userType]);

  useEffect(() => {
    fetchActivation();
  }, [fetchActivation]);

  useEffect(() => {
    window.addEventListener(refreshEvent, fetchActivation);
    return () => window.removeEventListener(refreshEvent, fetchActivation);
  }, [fetchActivation, refreshEvent]);

  return useMemo(() => {
    if (!activation) {
      return lastSidebarTasks[userType] || [];
    }
    if (activation.dismissed || isComplete(userType, activation)) {
      lastSidebarTasks[userType] = [];
      return [];
    }

    const progress = Math.round(getProgress(userType, activation));
    const nextTasks = getVisibleTasks(userType, activation)
      .filter((task) => !task.locked)
      .map((task, taskIndex) => {
        const progressLabel = taskIndex === 0 ? `Profile setup · ${progress}% complete` : '';
        const description = [progressLabel, task.microcopy].filter(Boolean).join(' — ');
        return {
          id: `profile-setup-${task.id}`,
          title: task.title,
          description,
          urgency: 'high',
          onClick: () => {
            navigateToOnboardingTask(navigateRef.current, locationRef.current, task);
            onNavigateRef.current?.();
          },
        };
      });
    lastSidebarTasks[userType] = nextTasks;
    return nextTasks;
  }, [activation, userType]);
}

export default useActivationSidebarTasks;
